<!-- settings.component.html -->
<div class="settings-page">
    <div class="main-header-container">
        <app-quotation-top-header></app-quotation-top-header>
        <app-quotation-header (collapsedState)="onHeaderCollapse($event)"></app-quotation-header>
    </div>

    <div class="settings-container" [class.expanded]="!isHeaderCollapsed" [class.collapsed]="isHeaderCollapsed">
        <!-- Header -->
        <div class="settings-header">
            <div class="header-content">
                <h1 class="page-title">
                    <i class="pi pi-cog"></i>
                    Settings
                </h1>
                <p class="page-subtitle">Manage your account settings and preferences</p>
            </div>
        </div>

        <!-- Tabs Navigation -->
        <div class="tabs-navigation">
            <div class="tabs-wrapper">
                <button *ngFor="let tab of tabs" class="tab-button" [class.active]="activeTab === tab.id"
                    (click)="selectTab(tab.id)">
                    <i class="pi" [ngClass]="tab.icon"></i>
                    <span>{{ tab.label }}</span>
                </button>
            </div>
        </div>

        <!-- Tab Content -->
        <div class="tab-content">

            <!-- Profile Tab -->
            <div class="tab-panel" *ngIf="activeTab === 'profile'">
                <div class="panel-header">
                    <h2>Profile Information</h2>
                    <p>Update your account profile information</p>
                </div>

                <form [formGroup]="profileForm" (ngSubmit)="onUpdateProfile()" class="settings-form">
                    <div class="form-section">
                        <div class="form-row">
                            <div class="form-group">
                                <label for="name" class="form-label">
                                    <i class="pi pi-user"></i>
                                    Full Name
                                </label>
                                <input type="text" id="name" formControlName="name" placeholder="Enter your full name"
                                    class="form-input" [class.error]="name?.invalid && name?.touched" />
                                <div class="error-message" *ngIf="name?.invalid && name?.touched">
                                    <i class="pi pi-exclamation-circle"></i>
                                    Name must be at least 3 characters
                                </div>
                            </div>

                            <div class="form-group">
                                <label for="email" class="form-label">
                                    <i class="pi pi-envelope"></i>
                                    Email Address
                                </label>
                                <input type="email" id="email" formControlName="email" placeholder="Enter your email"
                                    class="form-input" [class.error]="email?.invalid && email?.touched" />
                                <div class="error-message" *ngIf="email?.invalid && email?.touched">
                                    <i class="pi pi-exclamation-circle"></i>
                                    Please enter a valid email address
                                </div>
                            </div>
                        </div>

                        <div class="form-row">
                            <div class="form-group">
                                <label for="employeeNumber" class="form-label">
                                    <i class="pi pi-id-card"></i>
                                    Employee Number
                                </label>
                                <input type="text" id="employeeNumber" formControlName="employeeNumber"
                                    class="form-input disabled" />
                                <small class="form-hint">Employee number cannot be changed</small>
                            </div>

                            <div class="form-group">
                                <label for="role" class="form-label">
                                    <i class="pi pi-briefcase"></i>
                                    Role
                                </label>
                                <input type="text" id="role" formControlName="role" class="form-input disabled" />
                                <small class="form-hint">Role is managed by administrators</small>
                            </div>
                        </div>
                    </div>

                    <div class="form-actions">
                        <button type="submit" class="btn btn-primary" [disabled]="isLoading || profileForm.invalid">
                            <span *ngIf="!isLoading">
                                <i class="pi pi-check"></i>
                                Save Changes
                            </span>
                            <span *ngIf="isLoading" class="btn-loading">
                                <i class="pi pi-spin pi-spinner"></i>
                                Saving...
                            </span>
                        </button>
                    </div>
                </form>
            </div>
            <!-- Preferences Tab -->
            <div class="tab-panel" *ngIf="activeTab === 'preferences'">
                <div class="panel-header">
                    <h2>Application Preferences</h2>
                    <p>Customize your application experience</p>
                </div>

                <form [formGroup]="preferencesForm" class="settings-form">
                    <!-- Appearance Section -->
                    <div class="form-section">
                        <h3 class="section-title">
                            <i class="pi pi-palette"></i>
                            Appearance
                        </h3>

                        <div class="form-group">
                            <label for="theme" class="form-label">Theme</label>
                            <select id="theme" formControlName="theme" class="form-select">
                                <option *ngFor="let option of themeOptions" [value]="option.value">
                                    {{ option.label }}
                                </option>
                            </select>
                            <small class="form-hint">Choose your preferred color theme</small>
                        </div>

                        <div class="form-group">
                            <label for="language" class="form-label">Language</label>
                            <select id="language" formControlName="language" class="form-select">
                                <option *ngFor="let option of languageOptions" [value]="option.value">
                                    {{ option.label }}
                                </option>
                            </select>
                            <small class="form-hint">Select your preferred language</small>
                        </div>
                    </div>

                    <!-- Notifications Section -->
                    <div class="form-section">
                        <h3 class="section-title">
                            <i class="pi pi-bell"></i>
                            Notifications
                        </h3>

                        <div class="toggle-group">
                            <div class="toggle-item">
                                <div class="toggle-info">
                                    <label class="toggle-label">Email Notifications</label>
                                    <small class="toggle-description">Receive email updates about your account</small>
                                </div>
                                <label class="switch">
                                    <input type="checkbox" formControlName="emailNotifications">
                                    <span class="slider"></span>
                                </label>
                            </div>

                            <div class="toggle-item">
                                <div class="toggle-info">
                                    <label class="toggle-label">System Alerts</label>
                                    <small class="toggle-description">Get notified about important system
                                        updates</small>
                                </div>
                                <label class="switch">
                                    <input type="checkbox" formControlName="systemAlerts">
                                    <span class="slider"></span>
                                </label>
                            </div>

                            <div class="toggle-item">
                                <div class="toggle-info">
                                    <label class="toggle-label">Quotation Updates</label>
                                    <small class="toggle-description">Notifications when quotations are updated</small>
                                </div>
                                <label class="switch">
                                    <input type="checkbox" formControlName="quotationUpdates">
                                    <span class="slider"></span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <!-- General Section -->
                    <div class="form-section">
                        <h3 class="section-title">
                            <i class="pi pi-sliders-h"></i>
                            General
                        </h3>

                        <div class="toggle-group">
                            <div class="toggle-item">
                                <div class="toggle-info">
                                    <label class="toggle-label">Auto-save</label>
                                    <small class="toggle-description">Automatically save your work as you type</small>
                                </div>
                                <label class="switch">
                                    <input type="checkbox" formControlName="autoSave">
                                    <span class="slider"></span>
                                </label>
                            </div>

                            <div class="toggle-item">
                                <div class="toggle-info">
                                    <label class="toggle-label">Compact View</label>
                                    <small class="toggle-description">Use a more compact interface layout</small>
                                </div>
                                <label class="switch">
                                    <input type="checkbox" formControlName="compactView">
                                    <span class="slider"></span>
                                </label>
                            </div>

                            <div class="toggle-item">
                                <div class="toggle-info">
                                    <label class="toggle-label">Show Tutorials</label>
                                    <small class="toggle-description">Display helpful tips and tutorials</small>
                                </div>
                                <label class="switch">
                                    <input type="checkbox" formControlName="showTutorials">
                                    <span class="slider"></span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <div class="form-actions">
                        <button type="button" class="btn btn-primary" (click)="onSavePreferences()">
                            <i class="pi pi-check"></i>
                            Save Preferences
                        </button>
                    </div>
                </form>
            </div>

            <!-- Quick Access Tab -->
            <div class="tab-panel" *ngIf="activeTab === 'quick-access'">
                <div class="panel-header">
                    <h2>Quick Access</h2>
                    <p>Navigate to frequently used system tools and pages</p>
                </div>

                <div class="quick-access-grid">
                    <div *ngFor="let link of quickAccessLinks" class="quick-access-card"
                        (click)="navigateToLink(link.route)" [style.--card-color]="link.color">
                        <div class="card-icon">
                            <i class="pi" [ngClass]="link.icon"></i>
                        </div>
                        <div class="card-content">
                            <h3 class="card-title">{{ link.label }}</h3>
                            <p class="card-description">{{ link.description }}</p>
                        </div>
                        <div class="card-arrow">
                            <i class="pi pi-arrow-right"></i>
                        </div>
                    </div>
                </div>

                <!-- System Information Card -->
                <div class="system-info-card">
                    <div class="info-header">
                        <i class="pi pi-info-circle"></i>
                        <h3>System Information</h3>
                    </div>
                    <div class="info-grid">
                        <div class="info-item">
                            <span class="info-label">Version:</span>
                            <span class="info-value">v2.0.1</span>
                        </div>
                        <div class="info-item">
                            <span class="info-label">Last Login:</span>
                            <span class="info-value">{{ currentUser?.lastLogin || 'Today' }}</span>
                        </div>
                        <div class="info-item">
                            <span class="info-label">Account Status:</span>
                            <span class="info-value status-active">Active</span>
                        </div>
                        <div class="info-item">
                            <span class="info-label">User ID:</span>
                            <span class="info-value">{{ currentUser?.employeenumber || 'N/A' }}</span>
                        </div>
                    </div>
                </div>
            </div>

        </div>
    </div>

    <!-- Toast Messages -->
    <p-toast position="top-right"></p-toast>
</div>
